import { useState, useEffect, useCallback, Fragment } from 'react'
import { supabase } from '../../services/supabase'
import { Button } from '../../components/Common/Button'
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts'

const CHART_COLORS = ['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b', '#6366f1']

const today = () => new Date().toISOString().slice(0, 10)
const yearStart = () => `${new Date().getFullYear()}-01-01`
const monthStart = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}
const yearEnd = () => `${new Date().getFullYear()}-12-31`

const fmtR = (val) => {
  if (val === null || val === undefined || isNaN(val)) return 'R0.00'
  return `R${Number(val).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// Normalise split: DB may hold 0.2 (legacy import) or 20 — always display as whole %
const normPct = (v) => { const n = Number(v); return (n > 0 && n < 1) ? n * 100 : n }

const fmtDate = (val) => {
  if (!val) return '-'
  const d = new Date(val)
  if (isNaN(d)) return val
  return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')
}

function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4000)
    return () => clearTimeout(t)
  }, [onDismiss])
  return (
    <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded shadow-lg text-white text-sm font-semibold ${type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
      {message}
    </div>
  )
}

export function PolicyCommissionReport({ isDarkMode = false }) {
  const [startDate, setStartDate] = useState(yearStart())
  const [endDate, setEndDate] = useState(today())
  const [advisors, setAdvisors] = useState([])
  const [selectedAdvisorId, setSelectedAdvisorId] = useState('')
  const [reportData, setReportData] = useState([]) // [{advisorId, advisorName, commBase, commPct, commDue, policies:[...]}]
  const [expandedAdvisors, setExpandedAdvisors] = useState(new Set())
  const [loading, setLoading] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportLevel, setExportLevel] = useState('summary')
  const [toast, setToast] = useState(null)
  const [showCharts, setShowCharts] = useState(true)

  useEffect(() => {
    supabase.from('advisors').select('id, name, full_name').eq('active', true).order('name')
      .then(({ data }) => setAdvisors(data || []))
  }, [])

  const advisorLabel = (a) => a?.full_name || a?.name || 'Unnamed'

  const fetchReport = useCallback(async () => {
    setLoading(true)
    try {
      // Query 1: transactions for date range — filter by transaction_date (Remittance Date)
      const { data: txData, error: txErr } = await supabase
        .from('policy_transactions')
        .select('id, policy_invoice_no, transaction_date, statement_date, client, product, advisor_split, supplier_amount_excl, supplier_amount_vat, advisor_share_excl, advisor_share_vat, advisor_share_inc, active')
        .gte('transaction_date', startDate)
        .lte('transaction_date', endDate)
        .eq('active', true)
      if (txErr) throw txErr

      // Query 2: policies with their advisors and split percentages
      const { data: policiesData, error: polErr } = await supabase
        .from('policies')
        .select('id, policy_number, policy_advisors(advisor_id, split_percentage, advisors(id, name, full_name))')
      if (polErr) throw polErr

      // Build lookup: policy_number → {advisors: advisor[], advisorSplitMap: {advisorId: split%}}
      const policyAdvisorMap = {}
      for (const pol of policiesData || []) {
        if (!pol.policy_number) continue
        const normalizedPolNum = String(pol.policy_number).trim().toUpperCase()
        const advisors = (pol.policy_advisors || [])
          .map(pa => ({ ...pa.advisors, split_percentage: pa.split_percentage }))
          .filter(a => a.id)

        // Calculate sum of split percentages for normalization
        const splitSum = advisors.reduce((s, a) => s + (Number(a.split_percentage) || 0), 0)

        policyAdvisorMap[normalizedPolNum] = {
          advisors,
          splitSum,
          splitMap: advisors.reduce((m, a) => { m[a.id] = a.split_percentage; return m }, {})
        }
      }

      // Build advisor → policy → transactions map
      const advisorMap = {}

      for (const tx of txData || []) {
        const invoiceNo = tx.policy_invoice_no
        const normalizedInvoiceNo = String(invoiceNo || '').trim().toUpperCase()
        const policyData = normalizedInvoiceNo ? (policyAdvisorMap[normalizedInvoiceNo] || null) : null
        const advisorsForPolicy = policyData?.advisors || []

        // If no advisors linked, group under "Unassigned"
        const targets = advisorsForPolicy.length > 0
          ? advisorsForPolicy
          : [{ id: 'unassigned', name: 'Unassigned', full_name: 'Unassigned', split_percentage: null }]

        for (const adv of targets) {
          if (selectedAdvisorId && adv.id !== selectedAdvisorId) continue

          const advId = adv.id
          if (!advisorMap[advId]) {
            advisorMap[advId] = {
              advisorId: advId,
              advisorName: advisorLabel(adv),
              policyMap: {},
            }
          }

          const policyNum = invoiceNo || 'No Invoice No.'
          if (!advisorMap[advId].policyMap[policyNum]) {
            advisorMap[advId].policyMap[policyNum] = {
              policyNumber: policyNum,
              transactions: [],
              policyData,
              advisorSplitPct: policyData?.splitMap[advId] || null
            }
          }
          advisorMap[advId].policyMap[policyNum].transactions.push(tx)
        }
      }

      // Aggregate totals with commission split calculation
      const result = Object.values(advisorMap).map(adv => {
        const policies = Object.values(adv.policyMap).map(pol => {
          const txs = pol.transactions
          // Base commission is advisor_share_excl (what advisors actually earn to split)
          // NOT supplier_amount_excl (supplier's portion) — only advisor portion gets split
          const commBase = txs.reduce((s, t) => s + (Number(t.advisor_share_excl) || 0), 0)

          // Calculate advisor's commission share based on split percentage
          let commDue = 0
          if (pol.advisorSplitPct != null && pol.policyData?.splitSum > 0) {
            // Auto-calculate: advisor_commission = commBase × (advisor_split_percentage / sum_of_splits)
            // This handles both percentage (50, 100) and decimal (0.5, 1.0) formats
            commDue = commBase * (Number(pol.advisorSplitPct) / pol.policyData.splitSum)
          } else {
            // Fallback: use imported advisor_share_excl if no split percentage defined
            commDue = txs.reduce((s, t) => s + (Number(t.advisor_share_excl) || 0), 0)
          }

          // Get split percentage from policy_advisors (not from transaction)
          // Normalize to display as whole percentage (50 for 50%, not 0.5)
          const splitPct = pol.advisorSplitPct != null
            ? normPct(pol.advisorSplitPct)
            : null

          // Latest statement date in this policy group
          const stmtDate = txs.reduce((max, t) => (t.statement_date > max ? t.statement_date : max), txs[0]?.statement_date || '')
          const client = txs[0]?.client || ''
          const product = txs[0]?.product || ''
          const txCount = txs.length

          return { policyNumber: pol.policyNumber, commBase, commDue, splitPct, transactions: txs, stmtDate, client, product, txCount }
        })
        const commBase = policies.reduce((s, p) => s + p.commBase, 0)
        const commDue = policies.reduce((s, p) => s + p.commDue, 0)
        const avgSplit = policies.filter(p => p.splitPct != null).length > 0
          ? policies.filter(p => p.splitPct != null).reduce((s, p) => s + p.splitPct, 0) / policies.filter(p => p.splitPct != null).length
          : 0
        return { ...adv, policies, commBase, commDue, avgSplit }
      }).sort((a, b) => a.advisorName.localeCompare(b.advisorName))

      setReportData(result)
    } catch (err) {
      setToast({ message: `Error: ${err.message}`, type: 'error' })
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate, selectedAdvisorId])

  useEffect(() => { fetchReport() }, [fetchReport])

  const toggleExpand = (advId) => {
    setExpandedAdvisors(prev => {
      const next = new Set(prev)
      next.has(advId) ? next.delete(advId) : next.add(advId)
      return next
    })
  }

  const totalCommBase = reportData.reduce((s, a) => s + a.commBase, 0)
  // Commission Due shown in KPI = advisors only (excludes Unassigned bucket)
  const assignedData = reportData.filter(a => a.advisorId !== 'unassigned')
  const totalCommDue = assignedData.reduce((s, a) => s + a.commDue, 0)
  const unassignedCommDue = (reportData.find(a => a.advisorId === 'unassigned')?.commDue) || 0

  // ── EXPORT ────────────────────────────────────────────────────────────────
  const handleExport = async () => {
    const XLSX = await import('xlsx')
    const wb = XLSX.utils.book_new()
    const headerStyle = { font: { bold: true }, fill: { fgColor: { rgb: 'E6A83C' } } }

    const applyHeaderStyle = (ws) => {
      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1')
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cell = ws[XLSX.utils.encode_cell({ r: 0, c: C })]
        if (cell) cell.s = headerStyle
      }
    }

    const advisorFilter = selectedAdvisorId
      ? advisors.find(a => a.id === selectedAdvisorId)
      : null
    const advisorLabelStr = advisorFilter ? advisorLabel(advisorFilter) : 'All'

    // Tab 1: Summary
    const summaryRows = reportData.map(a => ({
      'Advisor': a.advisorName,
      'Comm Base': fmtR(a.commBase),
      'Avg Split %': a.avgSplit ? `${a.avgSplit.toFixed(1)}%` : '-',
      'Commission Due': fmtR(a.commDue),
    }))
    summaryRows.push({
      'Advisor': 'TOTAL',
      'Comm Base': fmtR(totalCommBase),
      'Avg Split %': '',
      'Commission Due': fmtR(totalCommDue),
    })
    const wsSum = XLSX.utils.json_to_sheet(summaryRows)
    applyHeaderStyle(wsSum)
    XLSX.utils.book_append_sheet(wb, wsSum, 'Summary')

    if (exportLevel === 'policy') {
      // Tab 2: Policy Breakdown
      const policyRows = []
      for (const adv of reportData) {
        for (const pol of adv.policies) {
          policyRows.push({
            'Advisor': adv.advisorName,
            'Policy Number': pol.policyNumber,
            'Stmt Date': pol.stmtDate ? fmtDate(pol.stmtDate) : '-',
            'Client': pol.client || '-',
            'Product': pol.product || '-',
            'Commission Base': fmtR(pol.commBase),
            'Split %': pol.splitPct != null ? `${pol.splitPct.toFixed(1)}%` : '-',
            'Commission Due': fmtR(pol.commDue),
          })
        }
        policyRows.push({
          'Advisor': `${adv.advisorName} SUBTOTAL`,
          'Policy Number': '', 'Stmt Date': '', 'Client': '', 'Product': '',
          'Commission Base': fmtR(adv.commBase),
          'Split %': '',
          'Commission Due': fmtR(adv.commDue),
        })
      }
      policyRows.push({
        'Advisor': 'GRAND TOTAL',
        'Policy Number': '', 'Stmt Date': '', 'Client': '', 'Product': '',
        'Commission Base': fmtR(totalCommBase),
        'Split %': '',
        'Commission Due': fmtR(totalCommDue),
      })
      const wsPol = XLSX.utils.json_to_sheet(policyRows)
      applyHeaderStyle(wsPol)
      XLSX.utils.book_append_sheet(wb, wsPol, 'Policy Breakdown')
    }

    if (exportLevel === 'policy' || exportLevel === 'detail' || exportLevel === 'full') {
      // Tab 2: Detail (advisor + policy)
      const detailRows = []
      for (const adv of reportData) {
        for (const pol of adv.policies) {
          detailRows.push({
            'Advisor': adv.advisorName,
            'Policy Number': pol.policyNumber,
            'Stmt Date': pol.stmtDate ? fmtDate(pol.stmtDate) : '-',
            'Client': pol.client || '-',
            'Product': pol.product || '-',
            'Comm Base': fmtR(pol.commBase),
            'Split %': pol.splitPct != null ? `${pol.splitPct.toFixed(1)}%` : '-',
            'Commission Due': fmtR(pol.commDue),
          })
        }
        detailRows.push({
          'Advisor': `${adv.advisorName} SUBTOTAL`,
          'Policy Number': '', 'Stmt Date': '', 'Client': '', 'Product': '',
          'Comm Base': fmtR(adv.commBase),
          'Split %': '',
          'Commission Due': fmtR(adv.commDue),
        })
      }
      const wsDet = XLSX.utils.json_to_sheet(detailRows)
      applyHeaderStyle(wsDet)
      XLSX.utils.book_append_sheet(wb, wsDet, 'Detail')
    }

    if (exportLevel === 'full') {
      // Tab 3: Full (advisor + policy + each transaction)
      const fullRows = []
      for (const adv of reportData) {
        for (const pol of adv.policies) {
          for (const tx of pol.transactions) {
            fullRows.push({
              'Advisor': adv.advisorName,
              'Policy Number': pol.policyNumber,
              'Transaction Date': fmtDate(tx.transaction_date),
              'Statement Date': fmtDate(tx.statement_date),
              'Invoice No': tx.policy_invoice_no || '',
              'Client': tx.client || '',
              'Product': tx.product || '',
              'Split %': tx.advisor_split != null ? `${normPct(tx.advisor_split)}%` : '-',
              'Supplier Excl': fmtR(tx.supplier_amount_excl),
              'Supplier VAT': fmtR(tx.supplier_amount_vat),
              'Adv Share Excl': fmtR(tx.advisor_share_excl),
              'Adv Share VAT': fmtR(tx.advisor_share_vat),
              'Adv Share Inc': fmtR(tx.advisor_share_inc),
            })
          }
        }
      }
      const wsFull = XLSX.utils.json_to_sheet(fullRows)
      applyHeaderStyle(wsFull)
      XLSX.utils.book_append_sheet(wb, wsFull, 'Full')
    }

    const filename = `CommissionReport_${advisorLabelStr}_${startDate}_${endDate}.xlsx`
    XLSX.writeFile(wb, filename)
    setShowExportModal(false)
    setToast({ message: `Exported: ${filename}`, type: 'success' })
  }

  return (
    <div className={`p-4 h-full flex flex-col gap-4 transition-colors duration-300 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}

      {/* Filters */}
      <div className={`p-4 flex-shrink-0 rounded border transition-colors duration-300 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
        <div className="flex flex-wrap gap-4 items-end">
          {/* Date range */}
          <div className="flex flex-col gap-1">
            <label htmlFor="report-start-date" className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Remittance Date From</label>
            <input id="report-start-date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="input-field text-sm" />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="report-end-date" className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Remittance Date To</label>
            <input id="report-end-date" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="input-field text-sm" />
          </div>

          {/* Quick date buttons */}
          <div className="flex gap-2 flex-wrap">
            {[
              { label: 'Month', fn: () => { setStartDate(monthStart()); setEndDate(today()) } },
              { label: 'YTD', fn: () => { setStartDate(yearStart()); setEndDate(today()) } },
              { label: 'Year', fn: () => { setStartDate(yearStart()); setEndDate(yearEnd()) } },
            ].map(btn => (
              <button key={btn.label} onClick={btn.fn} className="btn-secondary" style={{ padding: '5px 12px', fontSize: 12 }}>
                {btn.label}
              </button>
            ))}
          </div>

          {/* Advisor filter */}
          <div className="flex flex-col gap-1">
            <label htmlFor="report-advisor" className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Advisor</label>
            <select
              id="report-advisor"
              value={selectedAdvisorId}
              onChange={e => { setSelectedAdvisorId(e.target.value); setExpandedAdvisors(new Set()) }}
              className="input-field text-sm"
            >
              <option value="">All Advisors</option>
              {advisors.map(a => (
                <option key={a.id} value={a.id}>{advisorLabel(a)}</option>
              ))}
            </select>
          </div>

          <div className="ml-auto flex gap-2">
            <button
              onClick={() => setShowCharts(v => !v)}
              className="btn-secondary"
              style={{ padding: '5px 12px', fontSize: 12 }}
            >
              {showCharts ? '▲ Hide Charts' : '▼ Show Charts'}
            </button>
            <Button variant="secondary" onClick={() => setShowExportModal(true)}>
              ↓ Export Excel
            </Button>
          </div>
        </div>

        <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
          Showing commissions for remittance date <strong>{fmtDate(startDate)}</strong> to <strong>{fmtDate(endDate)}</strong>
          {selectedAdvisorId && advisors.find(a => a.id === selectedAdvisorId) &&
            <> — <strong>{advisorLabel(advisors.find(a => a.id === selectedAdvisorId))}</strong></>
          }
        </p>
      </div>

      {/* Charts & Summary — visible when toggled on and data exists */}
      {showCharts && !loading && reportData.length > 0 && (
        <div className="flex flex-col gap-4 flex-shrink-0">

          {/* KPI summary row */}
          <div className="grid grid-cols-3 gap-4">
            <div className={`p-4 text-center rounded-xl border transition-colors duration-300 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
              <p className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Commission Base</p>
              <p className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'}`}>{fmtR(totalCommBase)}</p>
              <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}>Total advisor share excl. VAT</p>
            </div>
            <div className="p-4 text-center rounded-xl bg-gradient-to-br from-sfp-orange to-orange-600 shadow-md">
              <p className="text-xs font-semibold uppercase tracking-wide text-orange-100">Commission Due to Advisors</p>
              <p className="text-2xl font-bold text-white mt-1">{fmtR(totalCommDue)}</p>
              <p className="text-xs text-orange-100 mt-0.5">
                Assigned advisors only excl. VAT
                {unassignedCommDue > 0 && <span className="block opacity-80 text-[10px]">({fmtR(unassignedCommDue)} unassigned)</span>}
              </p>
            </div>
            <div className={`p-4 text-center rounded-xl border transition-colors duration-300 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
              <p className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Advisors in Period</p>
              <p className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'}`}>{assignedData.length}</p>
              <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}>
                {assignedData.reduce((s, a) => s + a.policies.length, 0)} policies total
              </p>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            {/* Donut: commission due by advisor */}
            <div className={`p-4 rounded-xl border transition-colors duration-300 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
              <p className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'}`}>Commission Due by Advisor</p>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={reportData.map(a => ({ name: a.advisorName, value: a.commDue }))}
                    cx="50%"
                    cy="45%"
                    innerRadius={52}
                    outerRadius={85}
                    paddingAngle={3}
                    dataKey="value"
                    isAnimationActive
                    animationDuration={700}
                  >
                    {reportData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend
                    iconSize={8}
                    formatter={(v) => <span style={{ fontSize: 10, color: '#6b7280' }}>{v}</span>}
                  />
                  <Tooltip
                    formatter={(val) => [fmtR(val), 'Comm Due']}
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Grouped bar: commission base vs due */}
            <div className={`p-4 rounded-xl border transition-colors duration-300 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
              <p className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'}`}>Commission Base vs Due by Advisor</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={reportData.map(a => ({
                    name: a.advisorName.split(' ')[0],
                    'Comm Base': Math.round(a.commBase * 100) / 100,
                    'Comm Due': Math.round(a.commDue * 100) / 100,
                  }))}
                  margin={{ top: 5, right: 10, left: 0, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10, fill: '#9ca3af' }}
                    angle={-35}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: '#9ca3af' }}
                    tickFormatter={(v) => v >= 1000 ? `R${(v / 1000).toFixed(0)}k` : `R${v}`}
                  />
                  <Tooltip
                    formatter={(val, name) => [fmtR(val), name]}
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  />
                  <Legend
                    formatter={(v) => <span style={{ fontSize: 11, color: '#6b7280' }}>{v}</span>}
                  />
                  <Bar dataKey="Comm Base" fill="#94a3b8" radius={[4, 4, 0, 0]} isAnimationActive animationDuration={600} />
                  <Bar dataKey="Comm Due" fill="#f97316" radius={[4, 4, 0, 0]} isAnimationActive animationDuration={600} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Report Table */}
      <div className={`overflow-hidden flex flex-col flex-1 rounded border transition-colors duration-300 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
        <div className="overflow-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin h-6 w-6 border-4 border-sfp-orange border-t-transparent rounded-full" />
            </div>
          ) : reportData.length === 0 ? (
            <div className={`flex items-center justify-center h-32 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}>
              No commission data for the selected period
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b transition-colors duration-300 ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-100 border-gray-200'}`}>
                  <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider w-8 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}></th>
                  <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Advisor / Policy</th>
                  <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Stmt Date</th>
                  <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Client</th>
                  <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Product</th>
                  <th className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Comm Base</th>
                  <th className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Split %</th>
                  <th className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Commission Due</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map(adv => (
                  <Fragment key={adv.advisorId}>
                    {/* Advisor row */}
                    <tr
                      onClick={() => toggleExpand(adv.advisorId)}
                      className={`border-b cursor-pointer transition ${isDarkMode ? 'border-slate-700 hover:bg-slate-700' : 'border-gray-100 hover:bg-orange-50'}`}
                    >
                      <td className="px-4 py-3 text-center text-sfp-orange font-bold">
                        {expandedAdvisors.has(adv.advisorId) ? '▼' : '▶'}
                      </td>
                      <td className={`px-4 py-3 font-semibold ${isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'}`}>{adv.advisorName}</td>
                      <td className={`px-4 py-3 text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>{adv.policies.length} {adv.policies.length === 1 ? 'policy' : 'policies'}</td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3"></td>
                      <td className={`px-4 py-3 text-right ${isDarkMode ? 'text-slate-300' : 'text-sfp-darkgray'}`}>{fmtR(adv.commBase)}</td>
                      <td className={`px-4 py-3 text-right text-xs font-semibold ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                        {adv.avgSplit && adv.avgSplit > 0 ? `${adv.avgSplit.toFixed(1)}%` : '-'}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-sfp-orange">{fmtR(adv.commDue)}</td>
                    </tr>

                    {/* Drill-down: policies */}
                    {expandedAdvisors.has(adv.advisorId) && adv.policies.map(pol => (
                      <tr key={`${adv.advisorId}-${pol.policyNumber}`} className={`border-b ${isDarkMode ? 'bg-slate-750 border-slate-700' : 'bg-gray-50 border-gray-100'}`}>
                        <td className="px-4 py-2.5"></td>
                        <td className={`px-8 py-2.5 text-sm font-semibold font-mono ${isDarkMode ? 'text-slate-300' : 'text-sfp-darkgray'}`}>{pol.policyNumber}</td>
                        <td className={`px-4 py-2.5 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{fmtDate(pol.stmtDate)}</td>
                        <td className={`px-4 py-2.5 text-xs font-mono ${isDarkMode ? 'text-slate-300' : 'text-sfp-darkgray'} max-w-[140px] truncate`} title={pol.client}>{pol.client || '-'}</td>
                        <td className={`px-4 py-2.5 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'} max-w-[160px] truncate`} title={pol.product}>{pol.product || '-'}</td>
                        <td className={`px-4 py-2.5 text-right text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-sfp-darkgray'}`}>{fmtR(pol.commBase)}</td>
                        <td className={`px-4 py-2.5 text-right text-sm font-semibold ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                          {pol.splitPct != null ? `${pol.splitPct.toFixed(1)}%` : '-'}
                        </td>
                        <td className="px-4 py-2.5 text-right text-sm font-semibold text-sfp-orange">{fmtR(pol.commDue)}</td>
                      </tr>
                    ))}

                    {/* Subtotal row when expanded */}
                    {expandedAdvisors.has(adv.advisorId) && (
                      <tr key={`${adv.advisorId}-sub`} className={`border-b-2 ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-orange-50 border-sfp-orange'}`}>
                        <td className="px-4 py-2"></td>
                        <td className={`px-8 py-2 text-xs font-bold ${isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'}`}>SUBTOTAL — {adv.advisorName}</td>
                        <td className="px-4 py-2" colSpan={3}></td>
                        <td className={`px-4 py-2 text-right text-xs font-bold ${isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'}`}>{fmtR(adv.commBase)}</td>
                        <td className="px-4 py-2"></td>
                        <td className="px-4 py-2 text-right text-xs font-bold text-sfp-orange">{fmtR(adv.commDue)}</td>
                      </tr>
                    )}
                  </Fragment>
                ))}

                {/* Grand Total */}
                <tr className={isDarkMode ? 'bg-slate-700 text-slate-100 font-bold' : 'bg-sfp-darkgray text-white font-bold'}>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 uppercase tracking-wider text-sm">TOTAL</td>
                  <td className="px-4 py-3" colSpan={3}></td>
                  <td className="px-4 py-3 text-right">{fmtR(totalCommBase)}</td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 text-right text-yellow-300">{fmtR(totalCommDue)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`rounded-lg shadow-xl p-6 max-w-sm w-full mx-4 transition-colors duration-300 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
            <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'}`}>Export Commission Report</h3>
            <div className="space-y-3 mb-5">
              {[
                { value: 'summary', label: 'Summary', desc: 'Advisor totals only' },
                { value: 'policy', label: 'Policy Breakdown', desc: 'Advisor + Policy (with split % from policy assignment) + Commission Due' },
                { value: 'detail', label: 'Detail', desc: 'Advisor + policy breakdown with calculated commissions per split %' },
                { value: 'full', label: 'Full', desc: 'All details including each transaction amount' },
              ].map(opt => (
                <label key={opt.value} className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="exportLevel"
                    value={opt.value}
                    checked={exportLevel === opt.value}
                    onChange={() => setExportLevel(opt.value)}
                    className="mt-0.5"
                  />
                  <div>
                    <p className="text-sm font-semibold text-sfp-darkgray">{opt.label}</p>
                    <p className="text-xs text-gray-500">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-400 mb-4">
              Filename: CommissionReport_{selectedAdvisorId ? advisorLabel(advisors.find(a => a.id === selectedAdvisorId)) : 'All'}_{startDate}_{endDate}.xlsx
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowExportModal(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleExport} className="btn-primary">Download</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
